import { JOB_ROLE_TAGS } from '@/shared/config';

/**
 * 구조화 닉네임 — `직무태그#자유입력` (`회계#민지`).
 *
 * 닉네임 하나에 **소속 도메인**을 실어 보내는 것이 목적이다. 커뮤니티/채팅방에서
 * 대화만 봐도 상대가 어떤 일을 하는 사람인지 드러나야 「셀러#다현님, 재고 자동화 어떻게
 * 하셨어요?」 같은 교환이 저절로 생긴다. 운영 쪽에서도 방을 훑는 것만으로 직무 분포가
 * 보이고, 나중에 소분방을 쪼갤 때 태그로 초대 대상을 고를 수 있다.
 *
 * 그래서 태그는 **장식이 아니라 식별자**다. 사람마다 다르게 적으면 그 순간 쓸모가 없어지므로
 * 자유 입력이 아니라 `JOB_ROLE_TAGS` 에서 고르게 하고, 「기타」일 때만 직접 적게 한다.
 *
 * 태그와 자유입력을 **따로 들고 다니다가 표시할 때 합친다.** 합친 문자열 하나만 저장하면
 * 나중에 직무를 바꿀 때 앞부분을 잘라내 다시 붙여야 하는데, 그 파싱이 닉네임에 `#` 이
 * 섞이는 순간 깨진다. `#` 을 입력에서 막는 이유도 같다.
 */

/** 태그와 자유입력을 가르는 기호. */
export const NICKNAME_SEPARATOR = '#';

/**
 * 태그 길이 상한. 길면 채팅 목록에서 정작 이름이 밀려 나간다 —
 * `JOB_ROLE_TAGS` 중 가장 긴 「프리랜서」에 맞춘 값이다.
 */
export const NICKNAME_TAG_MAX = 5;

/** 자유입력 길이 상한. 합친 값이 `Member.name`(VarChar 20)에 들어가야 한다. */
export const NICKNAME_HANDLE_MAX = 12;

/**
 * 허용 문자 — 한글·영문·숫자만.
 *
 * 자모(`ㄱ-ㅎㅏ-ㅣ`)를 함께 허용한다. 한글 입력 중에는 조합이 끝나기 전 자모가 그대로
 * 값에 들어오므로, 빼면 타이핑하는 내내 에러가 깜빡인다.
 */
const NICKNAME_CHARS = /^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9]+$/;

const CHARS_MESSAGE = '한글·영문·숫자만 쓸 수 있습니다';

/** 닉네임을 이루는 값. 화면과 세션이 같은 모양으로 주고받는다. */
export type NicknameValue = {
  /** `JOB_ROLES` 의 값. 세그먼트를 가르는 축이라 태그와 별도로 남긴다 */
  jobRole: string;
  /** 실제로 닉네임 앞에 붙는 짧은 이름 */
  jobRoleTag: string;
  /** 자유입력 부분 */
  handle: string;
};

/**
 * 직무에 대응하는 기본 태그. 목록에 없거나 「기타」면 빈 문자열 — 직접 적어야 한다.
 *
 * Map 으로 한 번 옮겨 담는다. 객체를 임의의 문자열로 색인하면 타입상 항상 값이 있는 것처럼
 * 보여서, 목록에 없는 직무가 `undefined` 로 새어 나가는 걸 컴파일러가 못 잡는다.
 */
const TAG_BY_ROLE: ReadonlyMap<string, string> = new Map(Object.entries(JOB_ROLE_TAGS));

export function defaultTagFor(jobRole: string): string {
  return TAG_BY_ROLE.get(jobRole) ?? '';
}

/** 표시용 닉네임. 자유입력이 비면 보여줄 것이 없으므로 빈 문자열이다. */
export function composeNickname(tag: string, handle: string): string {
  const trimmedTag = tag.trim();
  const trimmedHandle = handle.trim();

  if (!trimmedHandle) return '';
  if (!trimmedTag) return trimmedHandle;

  return `${trimmedTag}${NICKNAME_SEPARATOR}${trimmedHandle}`;
}

export function validateNicknameHandle(value: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) return '닉네임을 입력해 주세요';
  if (trimmed.length > NICKNAME_HANDLE_MAX)
    return `닉네임은 ${NICKNAME_HANDLE_MAX}자까지 쓸 수 있습니다`;
  if (!NICKNAME_CHARS.test(trimmed)) return CHARS_MESSAGE;

  return null;
}

export function validateNicknameTag(value: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) return '직무 태그를 입력해 주세요';
  if (trimmed.length > NICKNAME_TAG_MAX) return `태그는 ${NICKNAME_TAG_MAX}자까지 쓸 수 있습니다`;
  if (!NICKNAME_CHARS.test(trimmed)) return CHARS_MESSAGE;

  return null;
}

/** 가입을 진행해도 되는 상태인지. 태그가 비면 닉네임이 도메인을 못 싣는다. */
export function isNicknameReady(value: NicknameValue): boolean {
  return (
    validateNicknameTag(value.jobRoleTag) === null && validateNicknameHandle(value.handle) === null
  );
}

/**
 * 회원의 표시 이름. 저장은 태그와 자유입력을 나눠서 하지만 **보여줄 때는 항상 합친 값**이다.
 * 화면마다 `${tag}#${name}` 을 직접 쓰면 태그 없는 회원에서 `#민지` 가 새어 나간다.
 */
export function memberNickname(member: { jobRoleTag: string; name: string }): string {
  return composeNickname(member.jobRoleTag, member.name);
}
